import { useMemo } from "react";
import type * as type from "@vTypes";
import type { CopyNurses } from "@/pages/roster-create/Roster_create";
import {
  dedupeShiftsByColumnGroup,
  getDefaultShiftsSortedBySequence,
  getShiftColumnGroupKey,
  getShiftMainTemplateCode,
  resolveShiftFromScheduleCellFromMaps,
  shouldPreferShiftDefinition,
  type ShiftLookupMaps,
} from "@/utils/shift-util";
import { getShiftGroupConfig } from "@/utils/shiftgb-name";
import type { ShiftCatalogShift } from "@/services/shift/shiftCatalog";

/** 시프트 정의 행 단위 집계 키 — 동일 shift_id 여러 DB 행 구분 */
export function shiftRowStorageKey(s: {
  id?: number;
  shift_id: string;
  default_shift?: string | null;
}): string {
  return getShiftColumnGroupKey(s);
}

export type SummaryItem = {
  shiftId: string;
  /** 시프트 정의 행 DB id — 동일 shift_id 다중 행일 때 색/이름 정확히 매칭 */
  shiftDbId?: number;
  data: number[];
  shift_gb: string;
  groupLength?: number;
  groupIndex?: number;
  groupDefaultShift?: string | null;
  name: string;
};

export type SummaryGroup = {
  group_shift_gb: string;
  items: SummaryItem[];
  groupLength: number;
  startIndex: number;
};

export type UseRosterSummaryStatsParams = {
  /** caller가 프리셉티 제외 등 전처리를 완료한 간호사 배열 */
  copyNurses: CopyNurses;
  /** caller가 use_mid 기반 M 필터 등을 완료한 시프트 코드 배열 */
  defaultShiftCode: type.availableShifts[];
  shiftLookupMaps: ShiftLookupMaps<ShiftCatalogShift>;
  year: number;
  month: number;
  useMid?: boolean;
  isExpanded: boolean;
  isSummaryMinimized: boolean;
  gridType?: "ROSTER_CREATE" | "ROSTER_ONLY_VIEW";
};

export type UseRosterSummaryStatsResult = {
  summaryRowGroups: SummaryGroup[];
  totalSummaryRowCount: number;
  lastSummaryRow: SummaryItem | null;
  orderedMainShiftsForStats: type.availableShifts[];
  mainShifts: type.availableShifts[];
  hasMoreShifts: boolean;
};

/**
 * 근무표 하단 통계(shift_gb 그룹핑 + 일별 집계 + 표시용 행 배열)를 계산하는 훅.
 *
 * caller가 이미 전처리한 데이터를 받는다:
 * - `copyNurses`: 프리셉티 제외 완료
 * - `defaultShiftCode`: use_mid 기반 M 필터 완료
 */
export function useRosterSummaryStats({
  copyNurses,
  defaultShiftCode,
  shiftLookupMaps,
  year,
  month,
  useMid = false,
  isExpanded,
  isSummaryMinimized,
  gridType = "ROSTER_CREATE",
}: UseRosterSummaryStatsParams): UseRosterSummaryStatsResult {
  const isRosterOnlyView = gridType === "ROSTER_ONLY_VIEW";

  const sourceShiftsForStats = useMemo(() => {
    return dedupeShiftsByColumnGroup(defaultShiftCode, { includeMid: useMid });
  }, [defaultShiftCode, useMid]);

  const mainShiftsForStats = useMemo(() => {
    const byKey = new Map<string, type.availableShifts>();
    getDefaultShiftsSortedBySequence(sourceShiftsForStats).forEach((shift) => {
      const key = getShiftColumnGroupKey(shift);
      const current = byKey.get(key);
      if (shouldPreferShiftDefinition(current, shift)) {
        byKey.set(key, shift);
      }
    });
    return [...byKey.values()];
  }, [sourceShiftsForStats]);

  const orderedMainShiftsForStats = useMemo(() => {
    if (!isRosterOnlyView) return mainShiftsForStats;
    const order = ["D", "E", "N", "M", "O"];
    const byDefault = new Map<string, (typeof mainShiftsForStats)[number]>();
    mainShiftsForStats.forEach((shift) => {
      const templateCode = getShiftMainTemplateCode(shift);
      if (templateCode) {
        byDefault.set(templateCode, shift);
      }
    });
    const fixed = order
      .map((code) => byDefault.get(code))
      .filter((s): s is (typeof mainShiftsForStats)[number] => !!s);
    const rest = mainShiftsForStats.filter(
      (s) => !order.includes(getShiftMainTemplateCode(s) ?? "")
    );
    return [...fixed, ...rest];
  }, [isRosterOnlyView, mainShiftsForStats]);

  // default_shift 있는 메인 시프트만 (서브코드 제외, sequence 순)
  const mainShifts = useMemo(
    () => getDefaultShiftsSortedBySequence(sourceShiftsForStats),
    [sourceShiftsForStats]
  );

  // 펼칠 수 있는 shift가 있는지 (메인 외 추가 시프트 존재 시)
  const hasMoreShifts = sourceShiftsForStats.length > mainShifts.length;

  // 1차: shift_gb 기준 그룹핑 + 일별 통계 계산
  const shiftGroupSummary = useMemo(() => {
    if (isSummaryMinimized) {
      return {};
    }
    if (
      !copyNurses ||
      copyNurses.length === 0 ||
      sourceShiftsForStats.length === 0
    ) {
      return {};
    }

    const daysInMonth = new Date(year, month, 0).getDate();

    const sortedShiftCode = sourceShiftsForStats;

    // shift 그룹핑 설정
    const shiftGroupConfig = getShiftGroupConfig();

    // 메인 그룹(O) 키: shift_gb가 null이어도 항상 메인 그룹으로 묶기 위한 기준값
    const mainGroupShift = sortedShiftCode.find(
      (shift) =>
        getShiftMainTemplateCode(shift) === shiftGroupConfig.MAIN_GROUP_CODE
    );
    const mainGroupKey =
      mainGroupShift?.shift_gb ?? shiftGroupConfig.MAIN_GROUP_CODE;

    // default_shift 기준으로 그룹핑, shift_gb를 키로 사용
    const shiftGroupsByGb: Record<
      string,
      {
        rowKey: string;
        shift_id: string;
        default_shift: string | null;
        sequence: number;
      }[]
    > = {};

    sortedShiftCode.forEach((shift) => {
      let shiftGb: string | null;
      const templateCode = getShiftMainTemplateCode(shift);

      // 메인 그룹과 서브코드는 같은 그룹으로 강제 묶음
      if (shiftGroupConfig.isInMainGroup(templateCode)) {
        shiftGb = mainGroupKey;
      } else if (templateCode) {
        shiftGb = shift.shift_gb ?? templateCode;
      } else {
        shiftGb = shift.shift_gb ?? null;
      }

      const groupKey = shiftGb ?? "null";

      if (!shiftGroupsByGb[groupKey]) {
        shiftGroupsByGb[groupKey] = [];
      }
      shiftGroupsByGb[groupKey].push({
        rowKey: shiftRowStorageKey(shift),
        shift_id: shift.shift_id,
        default_shift: templateCode,
        sequence: shift.sequence,
      });
    });

    // 각 그룹 내에서 sequence 기준으로 정렬
    Object.keys(shiftGroupsByGb).forEach((shiftGb) => {
      shiftGroupsByGb[shiftGb].sort(
        (a, b) => (a.sequence || 0) - (b.sequence || 0)
      );
    });

    // rowKey(시프트 정의 행) → 그룹 메타
    const rowKeyToGroupMap = new Map<
      string,
      { shiftGb: string; isDefault: boolean }
    >();
    Object.entries(shiftGroupsByGb).forEach(([shiftGb, shifts]) => {
      shifts.forEach((shift) => {
        rowKeyToGroupMap.set(shift.rowKey, {
          shiftGb,
          isDefault: !!shift.default_shift,
        });
      });
    });

    const allShiftCounts = new Map<string, number[]>();
    rowKeyToGroupMap.forEach((_, rowKey) => {
      allShiftCounts.set(rowKey, new Array(daysInMonth).fill(0));
    });

    // schedule + schedule_ids 로 셀별 시프트 행(id) 확정 → 통계 행과 일치
    copyNurses.forEach((nurse) => {
      const schedule = Array.isArray(nurse.schedule) ? nurse.schedule : [];
      const scheduleIds = (nurse as { schedule_ids?: (number | null)[] | null })
        .schedule_ids;
      schedule.forEach((raw, dayIndex) => {
        if (!raw || dayIndex >= daysInMonth) return;
        const scheduleCode = typeof raw === "string" ? raw : raw.code;
        if (!scheduleCode) return;
        const trimmed = scheduleCode.trim();
        if (!trimmed || trimmed === "-") return;
        const resolved = resolveShiftFromScheduleCellFromMaps(
          scheduleCode,
          scheduleIds?.[dayIndex] ?? null,
          shiftLookupMaps
        );
        if (!resolved) return;
        const rk = shiftRowStorageKey(resolved);
        if (rowKeyToGroupMap.has(rk)) {
          const counts = allShiftCounts.get(rk);
          if (counts) counts[dayIndex]++;
        }
      });
    });

    // 계산된 통계를 shift_gb별로 그룹핑하고 default/subCode로 분리
    const result: Record<
      string,
      {
        default: Record<string, number[]>;
        subCode: Record<string, number[]>;
      }
    > = {};

    Object.entries(shiftGroupsByGb).forEach(([shiftGb, shifts]) => {
      const defaultCounts: Record<string, number[]> = {};
      const subCodeCounts: Record<string, number[]> = {};

      shifts.forEach((shift) => {
        const counts = allShiftCounts.get(shift.rowKey);
        const dataToUse = counts || new Array(daysInMonth).fill(0);

        if (shiftGroupConfig.isSubCode(shift.default_shift)) {
          subCodeCounts[shift.rowKey] = dataToUse;
        } else if (shift.default_shift) {
          defaultCounts[shift.rowKey] = dataToUse;
        } else {
          subCodeCounts[shift.rowKey] = dataToUse;
        }
      });

      result[shiftGb] = {
        default: defaultCounts,
        subCode: subCodeCounts,
      };
    });

    return result;
  }, [
    copyNurses,
    sourceShiftsForStats,
    year,
    month,
    shiftLookupMaps,
    isSummaryMinimized,
  ]);

  // 데이터 없을 때 기본 표시 행
  const emptySummaryRows = useMemo(() => {
    const daysInMonth = new Date(year, month, 0).getDate();

    return orderedMainShiftsForStats
      .filter((s) => s.shift_id)
      .map((shift) => ({
        shiftId: shift.shift_id,
        shiftDbId: shift.id,
        data: Array(daysInMonth).fill(0),
        shift_gb: shift.shift_gb || "etc",
        groupLength: 1,
        groupIndex: 0,
        groupDefaultShift: getShiftMainTemplateCode(shift),
        name: shift.name,
      }));
  }, [orderedMainShiftsForStats, year, month]);

  // 2차: 표시용 그룹 배열 생성 (순서 + 펼침/접힘 적용)
  const summaryRowGroups = useMemo(() => {
    const getDirectMainTemplateCode = (item: SummaryItem): string | null => {
      return getShiftMainTemplateCode({
        shift_id: item.shiftId,
        default_shift: null,
      });
    };

    const normalizeDirectMainTemplateRows = (
      flatItems: SummaryItem[]
    ): SummaryItem[] => {
      const output: SummaryItem[] = [];
      const templateIndexMap = new Map<string, number>();

      flatItems.forEach((item) => {
        const templateCode =
          item.groupDefaultShift ?? getDirectMainTemplateCode(item);
        if (!templateCode) {
          output.push(item);
          return;
        }
        if (!useMid && templateCode === "M") return;

        const existingIndex = templateIndexMap.get(templateCode);
        if (existingIndex == null) {
          templateIndexMap.set(templateCode, output.length);
          output.push({
            ...item,
            groupDefaultShift: templateCode,
          });
          return;
        }

        const existing = output[existingIndex];
        if (!existing) return;
        const mergedData = existing.data.map(
          (value, index) => value + (item.data[index] ?? 0)
        );
        output[existingIndex] = {
          ...existing,
          data: mergedData,
          groupLength: Math.max(existing.groupLength ?? 1, item.groupLength ?? 1),
          groupIndex: Math.min(existing.groupIndex ?? 0, item.groupIndex ?? 0),
          shiftDbId: existing.shiftDbId ?? item.shiftDbId,
          name: existing.name || item.name,
        };
      });

      return output;
    };

    const toGrouped = (flatItems: SummaryItem[]): SummaryGroup[] => {
      const groups: SummaryGroup[] = [];
      let currentGroup: SummaryGroup | null = null;
      let currentIndex = 0;

      flatItems.forEach((item, index) => {
        const lastItem = currentGroup?.items[currentGroup.items.length - 1];
        const isNewGroup =
          !currentGroup ||
          lastItem?.shift_gb !== item.shift_gb ||
          (lastItem?.groupDefaultShift !== item.groupDefaultShift &&
            lastItem?.groupDefaultShift != null &&
            item.groupDefaultShift != null);

        if (isNewGroup) {
          if (currentGroup) groups.push(currentGroup);
          currentGroup = {
            group_shift_gb: item.shift_gb || `group-${index}`,
            items: [item],
            groupLength: item.groupLength || 1,
            startIndex: currentIndex,
          };
          currentIndex++;
        } else if (currentGroup) {
          currentGroup.items.push(item);
          currentGroup.groupLength = Math.max(
            currentGroup.groupLength,
            item.groupLength || 1
          );
          currentIndex++;
        }
      });
      if (currentGroup) groups.push(currentGroup);
      return groups;
    };

    if (isSummaryMinimized) {
      return [];
    }

    if (!shiftGroupSummary || Object.keys(shiftGroupSummary).length === 0) {
      return toGrouped(normalizeDirectMainTemplateRows(emptySummaryRows));
    }

    const result: SummaryItem[] = [];

    const daysInMonthForSummary = new Date(year, month, 0).getDate();

    const mainShiftOrder = orderedMainShiftsForStats
      .map((s) => getShiftMainTemplateCode(s))
      .filter((code): code is string => !!code);

    const hasNonZeroData = (data: number[]): boolean => {
      return data.some((value) => value > 0);
    };

    /** DB 행 키(id:…) → 시프트 정의 (동일 shift_id 여러 행 대비) */
    const rowKeyToShiftMap = new Map<string, type.availableShifts>();
    sourceShiftsForStats.forEach((shift) => {
      const rk = shiftRowStorageKey(shift);
      if (!rowKeyToShiftMap.has(rk)) {
        rowKeyToShiftMap.set(rk, shift);
      }
    });

    // default_shift 기준으로 shift_gb 매핑 생성 (DENO 찾기용)
    const defaultShiftToGbMap = new Map<string, string>();
    Object.entries(shiftGroupSummary).forEach(([shiftGb, groupData]) => {
      Object.keys(groupData.default).forEach((rowKey) => {
        const shift = rowKeyToShiftMap.get(rowKey);
        const templateCode = shift ? getShiftMainTemplateCode(shift) : null;
        if (templateCode && mainShiftOrder.includes(templateCode)) {
          defaultShiftToGbMap.set(templateCode, shiftGb);
        }
      });
    });
    const denoGbSet = new Set(defaultShiftToGbMap.values());

    if (isRosterOnlyView && !isExpanded) {
      const emptyRowByDefaultShift = new Map(
        emptySummaryRows
          .filter((row) => !!row.groupDefaultShift)
          .map((row) => [row.groupDefaultShift as string, row])
      );

      mainShiftOrder.forEach((defaultShift) => {
        const shiftGb = defaultShiftToGbMap.get(defaultShift);
        const groupData =
          shiftGb && shiftGroupSummary[shiftGb]
            ? shiftGroupSummary[shiftGb]
            : null;

        if (groupData && shiftGb) {
          const matchingDefaults = Object.entries(groupData.default).filter(
            ([rowKey]) => {
              const shift = rowKeyToShiftMap.get(rowKey);
              return shift ? getShiftMainTemplateCode(shift) === defaultShift : false;
            }
          );

          if (matchingDefaults.length > 0) {
            const mergedData = new Array(daysInMonthForSummary).fill(0);
            matchingDefaults.forEach(([, defaultData]) => {
              for (let i = 0; i < mergedData.length; i++) {
                mergedData[i] += defaultData[i] || 0;
              }
            });
            Object.entries(groupData.subCode).forEach(([, subCodeData]) => {
              for (let i = 0; i < mergedData.length; i++) {
                mergedData[i] += subCodeData[i] || 0;
              }
            });

            const firstRowKey = matchingDefaults[0][0];
            const shift = rowKeyToShiftMap.get(firstRowKey);
            result.push({
              shiftId: shift?.shift_id ?? defaultShift,
              shiftDbId: shift?.id,
              data: mergedData,
              shift_gb: shiftGb,
              groupLength: 1,
              groupIndex: 0,
              groupDefaultShift: defaultShift,
              name: shift?.name || "",
            });
            return;
          }
        }

        const fallback = emptyRowByDefaultShift.get(defaultShift);
        if (fallback) {
          result.push({
            shiftId: fallback.shiftId,
            data: fallback.data,
            shift_gb: fallback.shift_gb,
            groupLength: 1,
            groupIndex: 0,
            groupDefaultShift: defaultShift,
            name: fallback.name,
          });
        }
      });

      return toGrouped(normalizeDirectMainTemplateRows(result));
    }

    if (!isExpanded) {
      mainShiftOrder.forEach((defaultShift) => {
        const shiftGb = defaultShiftToGbMap.get(defaultShift);
        if (!shiftGb || !shiftGroupSummary[shiftGb]) return;

        const groupData = shiftGroupSummary[shiftGb];
        const matchingDefaults = Object.entries(groupData.default).filter(
          ([rowKey]) => {
            const shift = rowKeyToShiftMap.get(rowKey);
            return shift ? getShiftMainTemplateCode(shift) === defaultShift : false;
          }
        );
        if (matchingDefaults.length === 0) return;

        const mergedData = new Array(daysInMonthForSummary).fill(0);
        matchingDefaults.forEach(([, defaultData]) => {
          for (let i = 0; i < mergedData.length; i++) {
            mergedData[i] += defaultData[i] || 0;
          }
        });
        Object.entries(groupData.subCode).forEach(([, subCodeData]) => {
          for (let i = 0; i < mergedData.length; i++) {
            mergedData[i] += subCodeData[i] || 0;
          }
        });

        const firstShift = rowKeyToShiftMap.get(matchingDefaults[0][0]);
        result.push({
          shiftId: firstShift?.shift_id ?? defaultShift,
          shiftDbId: firstShift?.id,
          data: mergedData,
          shift_gb: shiftGb,
          groupLength: 1,
          groupIndex: 0,
          groupDefaultShift: defaultShift,
          name: firstShift?.name ?? "",
        });
      });
    } else {
      mainShiftOrder.forEach((defaultShift) => {
        const shiftGb = defaultShiftToGbMap.get(defaultShift);
        if (!shiftGb || !shiftGroupSummary[shiftGb]) return;

        const groupData = shiftGroupSummary[shiftGb];

        const defaultRowsForBand = Object.entries(groupData.default).filter(
          ([rowKey]) => {
            const shift = rowKeyToShiftMap.get(rowKey);
            return shift ? getShiftMainTemplateCode(shift) === defaultShift : false;
          }
        );

        const subCodeEntries = Object.entries(groupData.subCode);
        const visibleSubCodes = subCodeEntries.filter(([, data]) =>
          hasNonZeroData(data)
        );
        const groupItemsCount =
          defaultRowsForBand.length + visibleSubCodes.length;

        let currentGroupIndex = 0;

        defaultRowsForBand.forEach(([rowKey, defaultData]) => {
          const shift = rowKeyToShiftMap.get(rowKey);
          result.push({
            shiftId: shift?.shift_id ?? rowKey,
            shiftDbId: shift?.id,
            data: defaultData,
            shift_gb: shiftGb,
            groupLength: groupItemsCount,
            groupIndex: currentGroupIndex++,
            groupDefaultShift: defaultShift,
            name: shift?.name ?? "",
          });
        });

        subCodeEntries.forEach(([rowKey, data]) => {
          if (hasNonZeroData(data)) {
            const shift = rowKeyToShiftMap.get(rowKey);
            result.push({
              shiftId: shift?.shift_id ?? rowKey,
              shiftDbId: shift?.id,
              data,
              shift_gb: shiftGb,
              groupLength: groupItemsCount,
              groupIndex: currentGroupIndex++,
              groupDefaultShift: defaultShift,
              name: shift?.name || "",
            });
          }
        });
      });

      const nonDenoGroups = Object.entries(shiftGroupSummary).filter(
        ([shiftGb]) => {
          return !denoGbSet.has(shiftGb);
        }
      );

      nonDenoGroups.forEach(([shiftGb, groupData]) => {
        const defaultEntries = Object.entries(groupData.default);
        const subCodeEntries = Object.entries(groupData.subCode);
        const visibleSubCodes = subCodeEntries.filter(([, data]) =>
          hasNonZeroData(data)
        );
        const groupItemsCount = defaultEntries.length + visibleSubCodes.length;

        let currentGroupIndex = 0;

        defaultEntries.forEach(([rowKey, defaultData]) => {
          const shift = rowKeyToShiftMap.get(rowKey);
          result.push({
            shiftId: shift?.shift_id ?? rowKey,
            shiftDbId: shift?.id,
            data: defaultData,
            shift_gb: shiftGb,
            groupLength: groupItemsCount,
            groupIndex: currentGroupIndex++,
            groupDefaultShift: null,
            name: shift?.name || "",
          });
        });

        subCodeEntries.forEach(([rowKey, data]) => {
          if (hasNonZeroData(data)) {
            const shift = rowKeyToShiftMap.get(rowKey);
            result.push({
              shiftId: shift?.shift_id ?? rowKey,
              shiftDbId: shift?.id,
              data,
              shift_gb: shiftGb,
              groupLength: groupItemsCount,
              groupIndex: currentGroupIndex++,
              groupDefaultShift: null,
              name: shift?.name || "",
            });
          }
        });
      });
    }

    return toGrouped(normalizeDirectMainTemplateRows(result));
  }, [
    shiftGroupSummary,
    isExpanded,
    sourceShiftsForStats,
    emptySummaryRows,
    orderedMainShiftsForStats,
    isRosterOnlyView,
    year,
    month,
    isSummaryMinimized,
    useMid,
  ]);

  const totalSummaryRowCount = useMemo(
    () => summaryRowGroups.reduce((sum, group) => sum + group.items.length, 0),
    [summaryRowGroups]
  );

  const lastSummaryRow = useMemo(() => {
    const lastGroup = summaryRowGroups[summaryRowGroups.length - 1];
    return lastGroup?.items[lastGroup.items.length - 1] ?? null;
  }, [summaryRowGroups]);

  return {
    summaryRowGroups,
    totalSummaryRowCount,
    lastSummaryRow,
    orderedMainShiftsForStats,
    mainShifts,
    hasMoreShifts,
  };
}
